import { type ReactElement, useEffect, useState } from 'react';
import { type DirectoryRealm, type DirectoryUser, type AccountAuditEntry } from '@vello/api-client';
import { personName, providerServicesAuthority, riderWalletAuthority, shownEmail, shownPhone, statusTone } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Field, Grid, Notice, TextInput, DrawerHeader, DrawerBody, DrawerFooter, PageHeader, DetailGrid, DetailCell, Panel, PanelBody, PanelHead, SectionTitle, Spacer, Stack, Text } from '@vello/web-theme';
import { Drawer } from '@vello/web-theme';
import { AuditTimeline } from './DirectoryScreen';
import { PersonCourierPanel, PersonRidesPanel } from '../components/person-panels';
import { PersonAccountCard } from '../components/person-account-card';
import { PersonAccessCard } from '../components/person-access-card';
import { PersonActivityCard } from '../components/person-activity-card';
import { PersonKpis } from '../components/person-kpis';
import { PersonLegalCard } from '../components/person-legal-card';
import { PersonVerificationCard } from '../components/person-verification-card';
import { PersonRiderPhotoCard } from '../components/person-rider-photo-card';
import { PersonRatingsCard } from '../components/person-ratings-card';
import { PersonRelationshipsCard } from '../components/person-relationships-card';
import { PersonStandingCard } from '../components/person-standing-card';
import { PersonWalletCard } from '../components/person-wallet-card';
import { PersonSupportCard } from '../components/person-support-card';
import { usePersonDriverInsights, usePersonSessions, usePersonSummary } from '../components/person-reads';
import { DriverCharts, RiderCharts } from '../components/person-charts';
import { useCountryName } from '../i18n/country-name';
import { PersonPaymentMethodsPanel } from '../components/person-payment-methods';
import { PersonRegistryPanel } from '../components/person-registry';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';
import { ProviderServicesCard } from '../components/provider-services';

/**
 * The full account record (the design's "ficha completa", D319): back, name + status, contacts, the audited
 * suspend/reactivate, the KPI row and the account-action history. Only what the platform actually holds for the
 * account is rendered; nothing is fabricated.
 *
 * D580 made it a 360 view (99 / Uber benchmark): the KPI row (rides, cancellation rate, spend, rating, last access) and a grid
 * of cards — account data and registration, activity per service, recent rides, devices and accesses, standing (suspensions,
 * debts and cash block, safety flags), ratings and promotions, support and safety history, relationships, terms and privacy
 * — each in `components/person-*.tsx`, each reading its own route and answering for itself when that read is refused or fails.
 *
 * D401 replaced the "waiting on the reconciliation" placeholder with the real panels: the person's recent rides
 * (`POST /v1/ops/ride-support/rides/search`, D413, always with the market — without one the server reads the id as a RIDE id and
 * answers nothing) and, on a driver record, the courier jobs it holds in the selected market. The wallet panel the
 * design also draws still has no operator route and is not drawn.
 */
export function PersonScreen({ realm, listRoute }: { realm: DirectoryRealm; listRoute: string }): ReactElement {
  const { runtime, scope, grants } = useAdmin();
  const { params, goBack } = useRouter();
  const { toast, confirm, setRecordTitle } = useConsole();
  const { t } = useT();
  const { v } = useVocab();
  const countryName = useCountryName();
  const id = params.id ?? '';
  // D580: the two reads several cards draw from are read once here and handed down.
  const sessions = usePersonSessions(id, realm);
  const summary = usePersonSummary(id, realm);
  const insights = usePersonDriverInsights(id, realm);
  const [user, setUser] = useState<DirectoryUser | undefined>(undefined);
  const [audit, setAudit] = useState<readonly AccountAuditEntry[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = useState(false);

  const openEdit = (): void => {
    if (user === undefined) return;
    setForm({ firstName: user.firstName ?? '', lastName: user.lastName ?? '', email: user.email ?? '' });
    setEditing(true);
  };

  const saveEdit = async (): Promise<void> => {
    if (user === undefined) return;
    setSaving(true);
    const res = await runtime.adminUsers.update(user.id, { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() });
    setSaving(false);
    if (!res.ok) {
      toast({ message: t('person.edit.failed', { code: res.error.code }), icon: 'shieldAlert', tone: 'warning' });
      return;
    }
    setEditing(false);
    await load();
    toast({ message: t('person.edit.saved'), icon: 'check', tone: 'success' });
  };

  const load = async (): Promise<void> => {
    setError(undefined);
    const [u, a] = await Promise.all([runtime.adminUsers.get(id), runtime.adminUsers.audit(id)]);
    if (u.ok) {
      setUser(u.value.user);
      setRecordTitle(personName(u.value.user, scope.current.country));
    } else {
      setUser(undefined);
      setError(t('person.error', { code: u.error.code }));
    }
    setAudit(a.ok ? a.value.entries : []);
  };

  useEffect(() => {
    if (id !== '') void load();
    return () => setRecordTitle(undefined);
    // Load once per record id; the audit re-reads after an action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (target: 'ACTIVE' | 'SUSPENDED', reason: string | undefined, silent: boolean): Promise<void> => {
    if (user === undefined) return;
    setBusy(true);
    const res = await runtime.adminUsers.setStatus(user.id, target, reason);
    setBusy(false);
    if (!res.ok) {
      toast({ message: t('dir.actionFailed', { code: res.error.code }), icon: 'shieldAlert', tone: 'warning' });
      return;
    }
    await load();
    if (!silent) {
      const first = personName(user, scope.current.country).split(' ')[0] ?? '';
      toast({
        message: t(target === 'SUSPENDED' ? 'dir.toast.suspended' : 'dir.toast.reactivated', { name: first }),
        icon: target === 'SUSPENDED' ? 'shieldAlert' : 'check',
        tone: target === 'SUSPENDED' ? 'warning' : 'success',
        onUndo: () => void setStatus(target === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED', t('dir.undoReason'), true).then(() => toast({ message: t('dir.toast.undone'), icon: 'undo', tone: 'neutral' })),
      });
    }
  };

  const suspend = async (): Promise<void> => {
    if (user === undefined) return;
    const r = await confirm({
      title: t('dir.confirm.suspend.title', { name: personName(user, scope.current.country) }),
      body: t('dir.confirm.suspend.body'),
      confirmLabel: t('dir.confirm.suspend.confirm'),
      cancelLabel: t('ui.cancel'),
      icon: 'pause',
      reasons: [t('dir.reason.chargeback'), t('dir.reason.driverReport'), t('dir.reason.couponAbuse'), t('dir.reason.customerRequest')],
      reasonLabel: t('ui.reason'),
      notePlaceholder: t('dir.confirm.note'),
    });
    if (!r.confirmed) return;
    await setStatus('SUSPENDED', r.note !== undefined ? `${r.reason ?? ''} — ${r.note}` : r.reason, false);
  };

  if (error !== undefined) {
    return (
      <>
        <PageHeader title={t(realm === 'RIDER' ? 'nav.passengers' : 'nav.driverDirectory')} actions={<Button variant="ghost" icon="arrowLeft" onClick={() => goBack(listRoute)}>{t('person.back')}</Button>} />
        <Notice tone="danger">{error}</Notice>
      </>
    );
  }
  if (user === undefined) return <LoadingBlock />;

  const home = scope.current.country;
  const name = personName(user, home);
  const isRider = realm === 'RIDER';
  // The country is the console's scope, not a fact about the person: it is said only when this account is not in the scope's country.
  const foreign = home === undefined || home !== user.countryCode;

  return (
    <>
      <PageHeader
        title={name}
        badge={<Badge tone={statusTone(user.status)}>{v('identity.user_status', user.status)}</Badge>}
        subtitle={[shownPhone(user, home), shownEmail(user), foreign ? countryName(user.countryCode) : undefined].filter((part): part is string => part !== undefined && part !== '').join(' · ')}
        actions={
          <>
            <Button variant="ghost" icon="arrowLeft" onClick={() => goBack(listRoute)}>{t('person.back')}</Button>
            {/* D384: a masked record means the operator lacks `account:manage` — the edit and status actions would be refused. */}
            {user.contactMasked !== true && <Button variant="secondary" icon="pencil" onClick={openEdit}>{t('person.edit')}</Button>}
            {user.contactMasked !== true && user.status === 'ACTIVE' && <Button variant="danger" icon="pause" disabled={busy} onClick={() => void suspend()}>{t('dir.suspend.account')}</Button>}
            {user.contactMasked !== true && user.status === 'SUSPENDED' && <Button variant="success" icon="rotateCcw" disabled={busy} onClick={() => void setStatus('ACTIVE', undefined, false)}>{t('dir.reactivate.account')}</Button>}
          </>
        }
      />

      {/* D619 (owner 2026-10-18): the record reads top-down by importance — the person's indicators and charts (analytics database
          only), then the registration as the main block at full width, then every other panel in a masonry of columns (no hole
          under a short card), then the history. D580: every panel reads its own route and says on its own when it could not. */}
      <Stack gap={4} as="section" aria-label={t('prec.section.indicators')}>
        <SectionTitle actions={<Text role="caption" tone="secondary">{t('prec.section.indicators.note')}</Text>}>{t('prec.section.indicators')}</SectionTitle>
        <PersonKpis user={user} isRider={isRider} summary={summary.state} sessions={sessions.state} insights={insights.state} />
        {isRider ? <RiderCharts summary={summary.state} /> : <DriverCharts insights={insights.state} />}
      </Stack>

      <Stack gap={4} as="section" aria-label={t('prec.section.registration')}>
        <SectionTitle>{t('prec.section.registration')}</SectionTitle>
        {realm === 'DRIVER' ? (
          <PersonRegistryPanel personId={user.id} />
        ) : (
          <Grid min="lg">
            <PersonAccountCard user={user} />
            <PersonActivityCard summary={summary.state} reload={summary.reload} />
          </Grid>
        )}
      </Stack>

      <Stack gap={4} as="section" aria-label={t('prec.section.more')}>
        <SectionTitle>{t('prec.section.more')}</SectionTitle>
        <DetailGrid flow="masonry">
          {realm === 'DRIVER' && <DetailCell><PersonAccountCard user={user} /></DetailCell>}
          {/* D883 (program D871 phase 5): the service lines he asked for, each vehicle's approval per line, the duty session and the history. */}
          {realm === 'DRIVER' && providerServicesAuthority(grants).mayView && <DetailCell><ProviderServicesCard driverId={user.id} providerName={name} /></DetailCell>}
          {/* D401: the ride / courier panels the design draws, over the routes that already answer them. */}
          <DetailCell><PersonRidesPanel personId={user.id} realm={realm} /></DetailCell>
          {realm === 'DRIVER' && <DetailCell><PersonCourierPanel personId={user.id} /></DetailCell>}
          {realm === 'DRIVER' && <DetailCell><PersonPaymentMethodsPanel personId={user.id} /></DetailCell>}
          <DetailCell><PersonStandingCard user={user} isRider={isRider} /></DetailCell>
          {/* D737b (ADR-0150): the rider's signed Saldo Vello - credit minus open debt, the movements, the adjustments under four eyes (rider_wallet:view). */}
          {isRider && riderWalletAuthority(grants).mayView && <DetailCell><PersonWalletCard user={user} /></DetailCell>}
          {isRider && <DetailCell><PersonRatingsCard user={user} summary={summary.state} reload={summary.reload} /></DetailCell>}
          {/* D726 (doc 29 §6): the rider's verification badge and attempts; the images only with the privacy permission. */}
          {isRider && <DetailCell><PersonVerificationCard user={user} /></DetailCell>}
          {/* D725 (doc 29 §5 Q2): the rider's profile photo - the image only with the privacy permission; a rejection needs another operator's approval. */}
          {isRider && <DetailCell><PersonRiderPhotoCard user={user} /></DetailCell>}
          <DetailCell><PersonAccessCard sessions={sessions.state} reload={sessions.reload} /></DetailCell>
          <DetailCell><PersonSupportCard user={user} /></DetailCell>
          <DetailCell><PersonRelationshipsCard user={user} realm={realm} /></DetailCell>
          <DetailCell><PersonLegalCard user={user} /></DetailCell>
        </DetailGrid>
      </Stack>

      <Panel padded={false}>
        <PanelHead title={t('dir.tab.history')} actions={<Badge tone="neutral">{audit !== undefined ? t('person.actions.count', { count: String(audit.length) }) : '—'}</Badge>} />
        <PanelBody>
          <AuditTimeline entries={audit} />
        </PanelBody>
      </Panel>

      <Drawer open={editing} onClose={() => setEditing(false)} labelledBy="person-edit-title">
        <DrawerHeader titleId="person-edit-title" title={t('person.edit')} subtitle={t('person.edit.subtitle')} />
        <DrawerBody>
          <DrawerStack>
            <Field label={t('person.edit.firstName')} htmlFor="pe-first"><TextInput id="pe-first" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} /></Field>
            <Field label={t('person.edit.lastName')} htmlFor="pe-last"><TextInput id="pe-last" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} /></Field>
            <Field label={t('dir.col.email')} htmlFor="pe-email"><TextInput id="pe-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Field>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => setEditing(false)}>{t('ui.cancel')}</Button>
          <Spacer />
          <Button variant="primary" disabled={saving} onClick={() => void saveEdit()}>{t('person.edit.save')}</Button>
        </DrawerFooter>
      </Drawer>
    </>
  );
}
